package org.catharsis.spendtel.core.impl

import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FirebaseFirestore
import io.github.aakira.napier.Napier
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.ExpenseReportDocument
import org.catharsis.spendtel.feature.data.toExpenseReport
import org.catharsis.spendtel.feature.share.PublicShareDocument
import org.catharsis.spendtel.feature.share.generateShareId
import org.catharsis.spendtel.feature.share.toPublicShareSnapshot
import kotlin.time.Clock

private const val PUBLIC_SHARE_TTL_MS = 30L * 24L * 60L * 60L * 1000L

class ExpenseReportRepoImpl(
    private val firebaseFirestore: FirebaseFirestore,
) : org.catharsis.spendtel.core.api.ExpenseReportRepo {

    override suspend fun hasAnyExpenseReports(userId: String): Boolean {
        return getExpenseReports(userId).isNotEmpty()
    }

    override suspend fun getExpenseReports(userId: String): List<ExpenseReport> {
        return try {
            val collection = firebaseFirestore
                .collection("USERS")
                .document(userId)
                .collection("expenseReports")

            val snapshot = try {
                collection.orderBy("createdAt", Direction.DESCENDING).get()
            } catch (orderError: Exception) {
                Napier.w("Ordered expense report query failed, falling back: $orderError")
                collection.get()
            }

            snapshot.documents.mapNotNull { document ->
                try {
                    document.data<ExpenseReportDocument>().toExpenseReport(document.id)
                } catch (parseError: Exception) {
                    Napier.w("Could not parse expense report ${document.id}: $parseError")
                    null
                }
            }.sortedByDescending { it.createdAtMillis }
        } catch (e: Exception) {
            Napier.w("Could not load expense reports for $userId: $e")
            emptyList()
        }
    }

    override suspend fun getExpenseReport(userId: String, reportId: String): ExpenseReport? {
        return try {
            val document = firebaseFirestore
                .collection("USERS")
                .document(userId)
                .collection("expenseReports")
                .document(reportId)
                .get()

            if (!document.exists) return null

            document.data<ExpenseReportDocument>().toExpenseReport(document.id)
        } catch (e: Exception) {
            Napier.w("Could not load expense report $reportId for $userId: $e")
            null
        }
    }

    override fun listenToExpenseReport(userId: String, reportId: String): Flow<ExpenseReport?> {
        return firebaseFirestore
            .collection("USERS")
            .document(userId)
            .collection("expenseReports")
            .document(reportId)
            .snapshots
            .map { snapshot ->
                if (!snapshot.exists) return@map null

                runCatching {
                    snapshot.data<ExpenseReportDocument>().toExpenseReport(snapshot.id)
                }.getOrElse { error ->
                    Napier.w("Could not parse expense report $reportId: $error")
                    null
                }
            }
            .catchFirestorePermissionDenied("listenToExpenseReport(userId=$userId, reportId=$reportId)") { null }
    }

    override suspend fun deleteExpenseReport(userId: String, reportId: String): Result<Unit> {
        return try {
            firebaseFirestore
                .collection("USERS")
                .document(userId)
                .collection("expenseReports")
                .document(reportId)
                .delete()

            Result.success(Unit)
        } catch (e: Exception) {
            Napier.w("Could not delete expense report $reportId for $userId: $e")
            Result.failure(e)
        }
    }

    override suspend fun deleteAllExpenseReports(userId: String): Result<Unit> {
        return try {
            val collection = firebaseFirestore
                .collection("USERS")
                .document(userId)
                .collection("expenseReports")

            collection.get().documents.forEach { document ->
                document.reference.delete()
            }

            Result.success(Unit)
        } catch (e: Exception) {
            Napier.w("Could not delete expense reports for $userId: $e")
            Result.failure(e)
        }
    }

    override suspend fun createPublicShare(userId: String, report: ExpenseReport): Result<String> {
        return try {
            val shareId = generateShareId()
            val now = Clock.System.now().toEpochMilliseconds()
            val document = PublicShareDocument(
                ownerId = userId,
                reportId = report.id,
                createdAt = now,
                expiresAt = now + PUBLIC_SHARE_TTL_MS,
                snapshot = report.toPublicShareSnapshot(),
            )

            firebaseFirestore
                .collection("publicShares")
                .document(shareId)
                .set(document)

            Result.success(shareId)
        } catch (e: Exception) {
            Napier.w("Could not create public share for report ${report.id}: $e")
            Result.failure(e)
        }
    }

    override suspend fun getPublicShare(shareId: String): PublicShareDocument? {
        return try {
            val document = firebaseFirestore
                .collection("publicShares")
                .document(shareId)
                .get()

            if (!document.exists) return null

            val share = document.data<PublicShareDocument>()
            val now = Clock.System.now().toEpochMilliseconds()
            if (share.expiresAt <= now) return null

            share
        } catch (e: Exception) {
            Napier.w("Could not load public share $shareId: $e")
            null
        }
    }
}
